import { useNavigation } from '@react-navigation/native';
import {
    useEffect,
    useRef
} from 'react';
import { findNodeHandle } from 'react-native';
import RNExitApp from 'react-native-exit-app';
import EStyleSheet from 'react-native-extended-stylesheet';
import { useSelector } from 'react-redux';

import actions from '../../actions';
import {
    dataFrom,
    Page,
    Platform,
    React,
    Section,
    Text,
    TouchableWithoutFeedback,
    View
} from '../../common';
import { EndOfLifeApp } from '../../common/EndOfLifeApp';
import { InAppUpdater } from '../../common/InAppUpdater';
import { SwipeBack } from '../../common/SwipeBack';
import { syncUserState } from '../../init';
import {
    getIsEmailUnconfirmed,
    getMainScreenOptions,
    getNeedInAppUpdates,
    getUser,
    getUserInteractionError,
    isProfileEmpty,
    isProfileHaveRelatives,
    plannedVisits
} from '../../selectors';
import { getVisitForScroll } from '../../selectors/forms';
import { useIsOffine } from '../../services/http';
import { FamilyStoreLocalPanel } from '../Family/FamilyStoreLocalPanel';
import {
    DebugButton,
    QAModeButton
} from '../Services/Debug/DebugScreen';
import { PlannedVisitListItem } from '../Visits/VisitListItem';
import { VisitsList } from '../Visits/VisitsList';
import {
    AddVisitsIcon,
    Banner,
    PopularQueries,
    SectionWithVisitsLoading
} from './snippets';
import { ACodeReminder } from './snippets/ACodeReminder';
import { CloudConnectReminder } from './snippets/CloudConnectReminder';
import { EmailReminder } from './snippets/EmailReminder';
import { FavoritesListSection } from './snippets/Favorites';
import { InstallMobilePanel } from './snippets/InstallMobilePanel';
import { OfflineClinicWarning } from './snippets/OfflineClinicWarning';

export const SHOW_DEBUG_ON_HOME_SCREEN = false;
export const SHOW_QA_MODE_ON_HOME_SCREEN = false;

const PlannedVisitsList = dataFrom(plannedVisits, (props) => (
    <VisitsList
        paddingLeft={12}
        {...props}
        ListItem={PlannedVisitListItem}
        enableLoadFromClinic
        isInPastVisitScreen={false}
    />
));

const useErrorNotification = () => {
    const error = useSelector(getUserInteractionError);
    const isOffline = useIsOffine();
    if (error) {
        return error;
    }
    if (isOffline) {
        return Object.R('error.network_connection');
    }
    return null;
};

const PageContainer = ({
    children,
    onGetRef,
}) => {
    const error = useErrorNotification();
    return (
        <Page
            name="home"
            notification={error}
            onPullRefresh={syncUserState}
            hideOnBackground
            onGetRef={onGetRef}
        >
            {children}
        </Page>
    );
};

export const FillProfile = ({
    unconfirmedEmails,
    paddingLeft,
}) => {
    const navigation = useNavigation();
    const onClickFillProfileHandler = () => {
        navigation.navigate(unconfirmedEmails === true ? 'Account' : 'EditAccount');
    };

    const style = EStyleSheet.create({
        container: {
            paddingHorizontal: 12,
            paddingLeft: paddingLeft ?? 12,
        },
        link: {
            color: '$appTitleColor',
            fontSize: 14,
        },

        touch: {},

        text: {
            color: '$lightGray',
            fontSize: 14,
        },
    });

    return (
        <Text
            style={style.container}
            numberOfLines={0}
        >
            <Text style={style.text}>{Object.R('titles.fillProfileHint')}</Text>
            {Platform.OS === 'web' ? <Text
                style={style.link}
                onPress={onClickFillProfileHandler}>{Object.R('titles.fillProfileLink')}</Text> : <TouchableWithoutFeedback
                style={style.touch}
                onPress={onClickFillProfileHandler}
            >
                <Text style={style.link}>{Object.R('titles.fillProfileLink')}</Text>
            </TouchableWithoutFeedback>}
        </Text>
    );
};

/**
 * Home page.
 */
export const Home = Page.register(
    ({
        profileEmpty,
        unconfirmedEmails,
        plannedVisits: visits,
        user,
        haveRelatives,
    }) => {
        const componentRef = useRef();
        const scrollRef = useRef();
        const scroll = useSelector(getVisitForScroll);
        const updates = useSelector(getNeedInAppUpdates);
        const {
            shouldDisplayFavorites,
            shouldDisplayPopularQueries,
        } = useSelector(getMainScreenOptions);

        const scrollToVisit = () => {
            if (!scrollRef.current || !componentRef.current) {
                return;
            }
            componentRef?.current?.measureLayout?.(findNodeHandle(scrollRef.current), (x, y) => {
                scrollRef?.current?.scrollTo?.({
                    x: 0,
                    y,
                });
                actions.setFormValue('visitForScroll', null);
            });
        };

        useEffect(() => {
            if (!scrollRef.current || !componentRef.current || !scroll?.id) {
                return;
            }
            scrollToVisit();
        }, [ scroll?.id,
            componentRef.current,
            scrollRef.current ]);

        return (
            <SwipeBack
                onSwipeBack={() => {
                    RNExitApp.exitApp();
                }}
                enabled
                forOS={[ 'ios' ]}
            >
                <View style={{ flex: 1 }}>
                    <PageContainer
                        onGetRef={(r) => {
                            scrollRef.current = r;
                        }}
                    >
                        {SHOW_DEBUG_ON_HOME_SCREEN && <DebugButton/>}
                        {SHOW_QA_MODE_ON_HOME_SCREEN && <QAModeButton/>}
                        <OfflineClinicWarning/>
                        <ACodeReminder/>
                        <InstallMobilePanel/>
                        <CloudConnectReminder/>
                        {Platform.OS !== 'web' && (updates?.ios || updates?.android) && (
                            <InAppUpdater
                                useVersionCheck
                                permanentCancelUpdate
                                notifyPeriod={updates?.period}
                                elseComponent={<FamilyStoreLocalPanel/>}
                            />
                        )}
                        {/* <TestPermission /> */}
                        {Platform.OS === 'web' && <FamilyStoreLocalPanel/>}
                        <EmailReminder/>
                        <Section
                            ns="banner"
                            style={{ marginTop: 12 }}
                        >
                            <Banner/>
                        </Section>
                        {/* <SetReminder /> */}
                        {shouldDisplayPopularQueries ? (
                            <Section
                                ns="doctor_speciality"
                                title="titles.popularQueries"
                            >
                                <PopularQueries/>
                            </Section>
                        ) : null}
                        {shouldDisplayFavorites ? <FavoritesListSection/> : null}
                        <SectionWithVisitsLoading
                            title="titles.nearest_visits"
                            right={
                                (!profileEmpty || haveRelatives) &&
                                !user?.isLoading && (
                                    <AddVisitsIcon
                                        isFuture
                                        size={18}
                                        from={'Home'}
                                    />
                                )
                            }
                        >
                            {profileEmpty === true && (!visits || !visits?.length) ? (
                                user?.isLoading === false &&
                                (user?.isFetching === false || Platform.OS === 'web') && (
                                    <FillProfile unconfirmedEmails={unconfirmedEmails}/>
                                )
                            ) : (
                                <PlannedVisitsList
                                    onGetRef={(r) => {
                                        componentRef.current = r;
                                    }}
                                    refId={scroll?.id}
                                />
                            )}
                        </SectionWithVisitsLoading>
                        <EndOfLifeApp/>
                    </PageContainer>
                </View>
            </SwipeBack>
        );
    },
    {
        profileEmpty: { from: isProfileEmpty },
        unconfirmedEmails: { from: getIsEmailUnconfirmed },
        plannedVisits: { from: plannedVisits },
        user: { from: getUser },
        haveRelatives: { from: isProfileHaveRelatives },
    },
);
